import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const GeneralInfoTab = ({ contract, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: contract?.title,
    description: contract?.description,
    value: contract?.value,
    startDate: contract?.startDate,
    endDate: contract?.endDate,
    client: contract?.client,
    vendor: contract?.vendor,
    clientContact: contract?.clientContact,
    vendorContact: contract?.vendorContact,
    paymentTerms: contract?.paymentTerms,
    deliverables: contract?.deliverables,
    renewalTerms: contract?.renewalTerms
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onUpdate(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      title: contract?.title,
      description: contract?.description,
      value: contract?.value,
      startDate: contract?.startDate,
      endDate: contract?.endDate,
      client: contract?.client,
      vendor: contract?.vendor,
      clientContact: contract?.clientContact,
      vendorContact: contract?.vendorContact,
      paymentTerms: contract?.paymentTerms,
      deliverables: contract?.deliverables,
      renewalTerms: contract?.renewalTerms
    });
    setIsEditing(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  return (
    <div className="p-6 bg-background">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">General Information</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Basic contract details and party information
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Save"
                  iconPosition="left"
                  onClick={handleSave}
                >
                  Save Changes
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                iconName="Edit3"
                iconPosition="left"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Contract Details */}
          <div className="space-y-6">
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <Icon name="FileText" size={20} className="mr-2 text-primary" />
                Contract Details
              </h3>
              
              <div className="space-y-4">
                <Input
                  label="Contract Title"
                  type="text"
                  value={formData?.title}
                  onChange={(e) => handleInputChange('title', e?.target?.value)}
                  disabled={!isEditing}
                />
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData?.description}
                    onChange={(e) => handleInputChange('description', e?.target?.value)}
                    disabled={!isEditing}
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground disabled:bg-muted disabled:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Contract Value"
                    type="number"
                    value={formData?.value}
                    onChange={(e) => handleInputChange('value', parseFloat(e?.target?.value))}
                    disabled={!isEditing}
                  />
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Formatted Value
                    </label>
                    <div className="px-3 py-2 bg-muted rounded-md text-sm text-muted-foreground">
                      {formatCurrency(formData?.value)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Start Date"
                    type="date"
                    value={formData?.startDate}
                    onChange={(e) => handleInputChange('startDate', e?.target?.value)}
                    disabled={!isEditing}
                  />
                  <Input
                    label="End Date"
                    type="date"
                    value={formData?.endDate}
                    onChange={(e) => handleInputChange('endDate', e?.target?.value)}
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <Icon name="Scale" size={20} className="mr-2 text-primary" />
                Terms & Conditions
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Payment Terms
                  </label>
                  <textarea
                    value={formData?.paymentTerms}
                    onChange={(e) => handleInputChange('paymentTerms', e?.target?.value)}
                    disabled={!isEditing}
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground disabled:bg-muted disabled:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Key Deliverables
                  </label>
                  <textarea
                    value={formData?.deliverables}
                    onChange={(e) => handleInputChange('deliverables', e?.target?.value)}
                    disabled={!isEditing}
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground disabled:bg-muted disabled:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Renewal Terms
                  </label>
                  <textarea
                    value={formData?.renewalTerms}
                    onChange={(e) => handleInputChange('renewalTerms', e?.target?.value)}
                    disabled={!isEditing}
                    rows={2}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground disabled:bg-muted disabled:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Party Information */}
          <div className="space-y-6">
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <Icon name="Users" size={20} className="mr-2 text-primary" />
                Party Information
              </h3>
              
              <div className="space-y-6">
                {/* Client Information */}
                <div>
                  <h4 className="text-md font-medium text-foreground mb-3 flex items-center">
                    <Icon name="Building" size={16} className="mr-2 text-accent" />
                    Client
                  </h4>
                  <div className="space-y-3">
                    <Input
                      label="Company Name"
                      type="text"
                      value={formData?.client}
                      onChange={(e) => handleInputChange('client', e?.target?.value)}
                      disabled={!isEditing}
                    />
                    <Input
                      label="Primary Contact"
                      type="text"
                      value={formData?.clientContact}
                      onChange={(e) => handleInputChange('clientContact', e?.target?.value)}
                      disabled={!isEditing}
                    />
                  </div>
                </div>

                {/* Vendor Information */}
                <div>
                  <h4 className="text-md font-medium text-foreground mb-3 flex items-center">
                    <Icon name="Briefcase" size={16} className="mr-2 text-accent" />
                    Vendor
                  </h4>
                  <div className="space-y-3">
                    <Input
                      label="Company Name"
                      type="text"
                      value={formData?.vendor}
                      onChange={(e) => handleInputChange('vendor', e?.target?.value)}
                      disabled={!isEditing}
                    />
                    <Input
                      label="Primary Contact"
                      type="text"
                      value={formData?.vendorContact}
                      onChange={(e) => handleInputChange('vendorContact', e?.target?.value)}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <Icon name="BarChart3" size={20} className="mr-2 text-primary" />
                Contract Statistics
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">
                    {Math.ceil((new Date(formData.endDate) - new Date()) / (1000 * 60 * 60 * 24))}
                  </div>
                  <div className="text-sm text-muted-foreground">Days Remaining</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-success">85%</div>
                  <div className="text-sm text-muted-foreground">Completion</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-warning">3</div>
                  <div className="text-sm text-muted-foreground">Pending Items</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-accent">8</div>
                  <div className="text-sm text-muted-foreground">Documents</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralInfoTab;